// ZodiacCrush — Competitive Scoring (Candy-like)
// Goals:
// - Reward skill (creating/using specials, long lines, cross-shapes)
// - Keep results comparable across runs (stable, less RNG-spiky)
// - Make cascades meaningful but capped

// Base score by *longest straight line* involved in the match.
// (We avoid scoring purely by total group size to reduce RNG spikes from blobs.)
export function scoreForLineLen(lineLen) {
  const n = Math.max(0, Number(lineLen) || 0);
  if (n < 3) return 0;

  // Keep 3-match meaningful but not too high (leaderboard stability)
  if (n === 3) return 45;

  // 4+ should feel premium, but avoid runaway scores when specials become more common
  if (n === 4) return 130;
  if (n === 5) return 260;
  if (n === 6) return 360;

  // 7+: premium growth but not exponential
  return 360 + (n - 6) * 150;
}

// Bonus for cross / T / L style groups.
export function shapeBonusForGroup(g) {
  const maxRowRun = Number(g?.maxRowRun || 0);
  const maxColRun = Number(g?.maxColRun || 0);
  const isCross = maxRowRun >= 3 && maxColRun >= 3;
  return isCross ? 150 : 0;
}

// Bonus for *creating* a power-up (not activating it).
export function creationBonusForGroup(g) {
  // NOTE:
  // `g.powerup` can come either from the matcher (classic 4/5/TL rules)
  // OR from resolve.js when we spawn specials via “Carga Cósmica”.
  // That means 3-matches *can* legitimately have a powerup here.
  const p = g?.powerup;
  if (!p || !p.type) return 0;

  const t = String(p.type).toLowerCase();

  // Slightly lower than before because specials are more frequent now
  if (t.includes('stripe')) return 110;
  if (t.includes('bomb')) return 170;
  if (t.includes('star')) return 210;

  return 0;
}

// Cascade multiplier: competitive (stronger than casual), capped.
export function cascadeMultiplier(iter = 0) {
  const i = Math.max(0, Number(iter) || 0);
  const mult = 1 + i * 0.35; // 1.00, 1.35, 1.70, 2.05...
  return Math.min(mult, 2.5);
}

// Score from match groups (line length + shape + creation bonus)
export function sumMatchScores(matchGroups, { cascadeIter = 0 } = {}) {
  if (!Array.isArray(matchGroups) || matchGroups.length === 0) return 0;

  const base = matchGroups.reduce((acc, g) => {
    const maxRowRun = Number(g?.maxRowRun || 0);
    const maxColRun = Number(g?.maxColRun || 0);
    const lineLen = Math.max(maxRowRun, maxColRun);

    // Small bonus by group size (helps 3-matches feel less flat)
    // Capped to avoid RNG spikes from large connected blobs.
    const size = Math.max(0, Number(g?.size || 0));
    const sizeBonus = Math.min(70, Math.max(0, size - 3) * 12); // 3->0, 4->12, 5->24 ... cap 70

    return (
      acc +
      scoreForLineLen(lineLen) +
      sizeBonus +
      shapeBonusForGroup(g) +
      creationBonusForGroup(g)
    );
  }, 0);

  return Math.round(base * cascadeMultiplier(cascadeIter));
}

// Score for special activations (stripe/star/bomb).
// IMPORTANT: this expects `activated[]` items to include `clearedCount` (how many cells were added/cleared by the activation).
export function scoreForActivations(activated = [], { cascadeIter = 0 } = {}) {
  if (!Array.isArray(activated) || activated.length === 0) return 0;

  let raw = 0;

  for (const a of activated) {
    const s = a?.special;
    if (!s) continue;

    const key = String(s).toLowerCase();
    const clearedCount = Math.max(0, Number(a?.clearedCount || 0));

    if (key.includes('stripe')) {
      // Balance: activating a stripe that clears a full line should feel closer to a long-line match,
      // but still a bit lower to keep leaderboards stable.
      // Map clearedCount (typically up to board width/height) to a line-length score, then scale.
      const len = Math.max(3, Math.min(12, clearedCount));
      const lineScore = scoreForLineLen(len);

      // 0.55 keeps it premium without outscoring an equivalent raw 8+ match.
      raw += Math.round(lineScore * 0.55);
    } else if (key.includes('bomb')) {
      // Bomb should feel premium but still stable (3x3 typical ~9 cells)
      raw += 130 + clearedCount * 18;
    } else if (key.includes('star')) {
      // Star can clear many cells; keep it strong but controlled
      raw += 150 + clearedCount * 20;
    }
  }

  return Math.round(raw * cascadeMultiplier(cascadeIter));
}